import type { IDropdownOption } from "@fluentui/react";
import { Dropdown, Stack } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import type { NodeProps, Node } from "@xyflow/react";
import type { ExtraNodeData, InterfaceToRecord } from "./action-config-node";
import ActionConfigNode from "./action-config-node";
import type { SetDynamicFlagData } from "../../../api";
import { StateVariableAction, DynamicFlag, FlagCanId } from "../../../api";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useUpdateNodeData } from "@/lib/hooks/reactflow";

type SetDynamicFlagNode = Node<InterfaceToRecord<SetDynamicFlagData & ExtraNodeData>>;

export default function SetDynamicFlag(props: NodeProps<SetDynamicFlagNode>): JSX.Element {
    const { t }: { t: TFunction } = useTranslation();

    const [flag, setFlag] = useState(props.data.flag ?? DynamicFlag.ZERO);
    const [action, setAction] = useState(props.data.action ?? StateVariableAction.TOGGLE);
    const [canId, setCanId] = useState(props.data.canId ?? FlagCanId.ALL_ANTENNAS);
    const updateNode = useUpdateNodeData<SetDynamicFlagNode>(props.id);

    // We have to set the data to prevent undefined values
    useEffect(() => {
        updateNode({ flag, action, canId });
    }, [flag, action, canId, updateNode]);

    const dropdownOptionsDynamicFlag: IDropdownOption[] = Object.values(DynamicFlag).map(
        (key, index) => ({
            key,
            text: t(`component.actionConfigNodes.setDynamicFlag.dynamicFlag.${index}`),
        }),
    );
    const dropdownOptionsAction: IDropdownOption[] = Object.values(StateVariableAction).map(
        (key, index) => ({
            key,
            text: t(`component.actionConfigNodes.action.${index}`),
        }),
    );
    const dropdownOptionsFlagCanId: IDropdownOption[] = Object.values(FlagCanId).map(
        (key, index) => ({
            key,
            text: t(`component.actionConfigNodes.setFlag.canId.${index}`),
        }),
    );

    return (
        <ActionConfigNode nodeKind={props.type} {...props.data}>
            <Stack
                tokens={{
                    childrenGap: "2em",
                }}
            >
                <Dropdown
                    options={dropdownOptionsDynamicFlag}
                    selectedKey={flag}
                    onChange={(_e, option) => setFlag(option?.key as DynamicFlag)}
                    styles={{ dropdown: { width: 200 } }}
                />
                <Dropdown
                    options={dropdownOptionsAction}
                    selectedKey={action}
                    onChange={(_e, option) => setAction(option?.key as StateVariableAction)}
                    styles={{ dropdown: { width: 200 } }}
                />
                <Dropdown
                    label={t("component.actionConfigNodes.setDynamicFlag.canId.label")}
                    options={dropdownOptionsFlagCanId}
                    selectedKey={canId}
                    onChange={(_e, option) => setCanId(option?.key as FlagCanId)}
                    styles={{ dropdown: { width: 200 } }}
                />
            </Stack>
        </ActionConfigNode>
    );
}
